import { useEffect, useMemo, useRef, useState } from 'react'
import type { Campaign, Page, RegisteredAccount, Role, SystemNotification, Task, UserProfile } from '../types'
import type { AuthenticatedUser } from '../auth'
import { mockUsers, users, type UserKey } from '../data'
import { taskBadgeLabel } from '../taskBadge'
import { canAccessTalentDesk } from '../talentDesk'
import { Brand } from './Brand'
import { Icon } from './Icon'
import { UserAvatar } from './UserAvatar'

interface LayoutProps {
  children: React.ReactNode
  page: Page
  role: Role
  simulatedUserKey: UserKey | null
  userProfiles: Record<string, UserProfile>
  currentUserProfile: UserProfile
  notifications: SystemNotification[]
  campaigns: Campaign[]
  tasks: Task[]
  accounts: RegisteredAccount[]
  authenticatedUser: AuthenticatedUser
  setPage: (page: Page) => void
  setTestUser: (userKey: UserKey | null) => void
  onOpenNotification: (notification: SystemNotification) => void
  onMarkAllNotificationsRead: () => void
  onOpenSearchCampaign: (campaignId: string) => void
  onOpenSearchTask: (taskId: string) => void
  onOpenSearchPerson: (email: string) => void
  logout: () => void
}

export interface GlobalSearchResult {
  id: string
  type: 'campaign' | 'task' | 'person'
  title: string
  subtitle: string
  initials?: string
  picture?: string
}

function normalizedSearchText(value: string) {
  return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
}

export function globalSearchResults(query: string, campaigns: Campaign[], tasks: Task[], accounts: RegisteredAccount[]) {
  const term = normalizedSearchText(query.trim())
  if (!term) return []
  const includes = (...values: Array<string | undefined>) => normalizedSearchText(values.filter(Boolean).join(' ')).includes(term)
  const campaignNames = new Map(campaigns.map(campaign => [campaign.id, campaign.name]))
  const campaignResults: GlobalSearchResult[] = campaigns.filter(campaign => includes(campaign.name, campaign.code, campaign.operation, campaign.reportArea)).slice(0, 4).map(campaign => ({
    id: campaign.id, type: 'campaign', title: campaign.name, subtitle: [campaign.code, campaign.operation, campaign.reportArea].filter(Boolean).join(' · '),
  }))
  const taskResults: GlobalSearchResult[] = tasks.filter(task => includes(task.title, task.description, task.owner, task.status, campaignNames.get(task.campaignId))).slice(0, 6).map(task => ({
    id: task.id, type: 'task', title: task.title, subtitle: `${campaignNames.get(task.campaignId) ?? 'Campanha'} · ${task.status}`,
  }))
  const peopleResults: GlobalSearchResult[] = accounts.filter(account => includes(account.name, account.email, account.profile, account.initials)).slice(0, 4).map(account => ({
    id: account.email, type: 'person', title: account.name, subtitle: `${account.profile} · ${account.email}`, initials: account.initials, picture: account.picture,
  }))
  return [...campaignResults, ...taskResults, ...peopleResults]
}

const navigation: Array<{ id: Page; label: string; icon: Parameters<typeof Icon>[0]['name']; managementOnly?: boolean }> = [
  { id: 'dashboard', label: 'Visão geral', icon: 'grid' },
  { id: 'tasks', label: 'Campanhas', icon: 'check' },
  { id: 'reports', label: 'Relatórios & BI', icon: 'report' },
  { id: 'talentDesk', label: 'Banco de Talentos', icon: 'usersGroup', managementOnly: true },
]

export function Layout({ children, page, role, simulatedUserKey, userProfiles, currentUserProfile, notifications, campaigns, tasks, accounts, authenticatedUser, setPage, setTestUser, onOpenNotification, onMarkAllNotificationsRead, onOpenSearchCampaign, onOpenSearchTask, onOpenSearchPerson, logout }: LayoutProps) {
  const [searchQuery, setSearchQuery] = useState('')
  const [searchOpen, setSearchOpen] = useState(false)
  const [activeSearchResult, setActiveSearchResult] = useState(0)
  const searchRef = useRef<HTMLDivElement>(null)
  const fallbackUser = users[role]
  const simulatedUser = simulatedUserKey ? users[simulatedUserKey] : undefined
  const displayedName = simulatedUser?.name ?? authenticatedUser.name
  const displayedEmail = simulatedUser?.email ?? authenticatedUser.email
  const initials = displayedName.split(/\s+/).filter(Boolean).slice(0, 2).map(part => part[0]).join('').toUpperCase() || fallbackUser.initials
  const canSimulateUsers = import.meta.env.DEV && authenticatedUser.systemRole === 'admin'
  const unreadNotifications = notifications.filter(notification => !notification.readAt).length
  const searchResults = useMemo(() => globalSearchResults(searchQuery, campaigns, tasks, accounts), [searchQuery, campaigns, tasks, accounts])

  useEffect(() => {
    const closeSearch = (event: PointerEvent) => {
      if (!(event.target instanceof Node) || !searchRef.current?.contains(event.target)) setSearchOpen(false)
    }
    document.addEventListener('pointerdown', closeSearch)
    return () => document.removeEventListener('pointerdown', closeSearch)
  }, [])

  function selectSearchResult(result: GlobalSearchResult) {
    if (result.type === 'campaign') onOpenSearchCampaign(result.id)
    else if (result.type === 'task') onOpenSearchTask(result.id)
    else onOpenSearchPerson(result.id)
    setSearchQuery('')
    setSearchOpen(false)
    setActiveSearchResult(0)
  }

  function handleSearchKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Escape') {
      setSearchOpen(false)
      return
    }
    if (!searchResults.length) return
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault()
      const direction = event.key === 'ArrowDown' ? 1 : -1
      setActiveSearchResult(index => (index + direction + searchResults.length) % searchResults.length)
      setSearchOpen(true)
    } else if (event.key === 'Enter') {
      event.preventDefault()
      selectSearchResult(searchResults[activeSearchResult] ?? searchResults[0])
    }
  }

  return <div className="shell">
    <aside className="sidebar" id="sidebar">
      <Brand />
      <div className="nav-label">Trabalho</div>
      <nav>{navigation.filter(item => !item.managementOnly || canAccessTalentDesk(currentUserProfile)).map(item => <button key={item.id} className={`nav-item ${page === item.id ? 'active' : ''}`} onClick={() => setPage(item.id)}><Icon name={item.icon}/><span>{item.label}</span>{item.id === 'tasks' && unreadNotifications > 0 && <span className="nav-badge" aria-label={`${unreadNotifications} notificações de tarefas não lidas`}>{taskBadgeLabel(unreadNotifications)}</span>}</button>)}</nav>
      {role === 'admin' && <div className="nav-label">Administração</div>}
      <button className={`nav-item ${page === 'settings' ? 'active' : ''}`} onClick={() => setPage('settings')}><Icon name="settings"/><span>Configurações</span></button>
      <div className="sidebar-user"><UserAvatar initials={initials} name={displayedName} picture={simulatedUserKey ? undefined : authenticatedUser.picture} fallbackPicture={simulatedUserKey ? undefined : authenticatedUser.avatarUrl} className="avatar user-photo"/><div><b>{displayedName}</b><small>{currentUserProfile}{simulatedUserKey ? ' · mock' : ''}</small></div><button className="logout" title="Sair" onClick={logout}><Icon name="logout"/></button></div>
    </aside>
    <section className="main">
      <header className="topbar">
        <button className="icon-btn mobile-menu" onClick={() => document.querySelector('#sidebar')?.classList.toggle('open')}>☰</button>
        <div className="search" ref={searchRef}><Icon name="search"/><input value={searchQuery} onChange={event => { setSearchQuery(event.target.value); setSearchOpen(true); setActiveSearchResult(0) }} onFocus={() => setSearchOpen(true)} onKeyDown={handleSearchKeyDown} placeholder="Buscar campanhas, tarefas ou pessoas..." role="combobox" aria-label="Busca global" aria-autocomplete="list" aria-expanded={searchOpen && Boolean(searchQuery.trim())} aria-controls="global-search-results"/>{searchOpen && searchQuery.trim() && <div className="global-search-results" id="global-search-results" role="listbox">{searchResults.length ? searchResults.map((result, index) => <button type="button" role="option" aria-selected={index === activeSearchResult} className={index === activeSearchResult ? 'active' : ''} key={`${result.type}-${result.id}`} onMouseEnter={() => setActiveSearchResult(index)} onClick={() => selectSearchResult(result)}>{result.type === 'person' ? <UserAvatar initials={result.initials ?? ''} name={result.title} picture={result.picture} className="global-search-avatar"/> : <span className={`global-search-icon ${result.type}`}><Icon name={result.type === 'campaign' ? 'folder' : 'check'}/></span>}<span><b>{result.title}</b><small>{result.subtitle}</small></span><em>{result.type === 'campaign' ? 'Campanha' : result.type === 'task' ? 'Tarefa' : 'Pessoa'}</em></button>) : <div className="global-search-empty"><Icon name="search"/><b>Nenhum resultado encontrado</b><span>Tente buscar por outro nome, código ou e-mail.</span></div>}</div>}</div>
        <div className="top-actions">
          {canSimulateUsers ? <select className={`role-switch ${simulatedUserKey ? 'simulating' : ''}`} value={simulatedUserKey ?? 'real'} onChange={event => setTestUser(event.target.value === 'real' ? null : event.target.value as UserKey)} title="Simular usuário mock">
            <option value="real">Minha conta · Administrador</option>
            <optgroup label="Usuários mocks">{mockUsers.map(([accountKey, account]) => <option key={accountKey} value={accountKey}>{account.name} · {userProfiles[accountKey]}</option>)}</optgroup>
          </select> : <div className="authenticated-account" title={authenticatedUser.email}><span className="account-status"/>{authenticatedUser.email}</div>}
          {simulatedUserKey && <div className="mock-account" title={`Sessão real: ${authenticatedUser.email}`}><span/>Mock: {displayedEmail}</div>}
          <details className="notifications-menu">
            <summary className="icon-btn" title="Notificações" aria-label={`${unreadNotifications} notificações não lidas`}><Icon name="bell"/>{unreadNotifications > 0 && <><span className="ping"/><b className="notification-count">{unreadNotifications > 9 ? '9+' : unreadNotifications}</b></>}</summary>
            <div className="notifications-popover">
              <div className="notifications-head"><div><strong>Notificações</strong><small>{unreadNotifications ? `${unreadNotifications} não ${unreadNotifications === 1 ? 'lida' : 'lidas'}` : 'Tudo em dia'}</small></div>{unreadNotifications > 0 && <button type="button" onClick={onMarkAllNotificationsRead}>Marcar todas como lidas</button>}</div>
              <div className="notifications-list">{notifications.length ? notifications.map(notification => <button type="button" className={notification.readAt ? '' : 'unread'} key={notification.id} onClick={event => { event.currentTarget.closest('details')?.removeAttribute('open'); onOpenNotification(notification) }}><span className="notification-icon"><Icon name="check"/></span><span><strong>{notification.title}</strong><small>{notification.message}</small><time>{notification.createdAt}</time></span></button>) : <div className="notifications-empty"><Icon name="bell"/><strong>Nenhuma notificação</strong><span>Novas atribuições e campanhas para análise aparecerão aqui.</span></div>}</div>
            </div>
          </details>
        </div>
      </header>
      <div className="content">{children}</div>
    </section>
  </div>
}
